import React, { useState, useRef, useEffect } from 'react';
import { useNavigate, Link, useSearchParams } from 'react-router-dom';
import {
  FiUser,
  FiMail,
  FiLock,
  FiShield,
  FiUserPlus,
  FiCheck,
  FiX,
  FiInfo,
  FiFileText,
  FiCamera,
} from 'react-icons/fi';
import { useApi } from '../../hooks/useApi';
import { useAuthStore } from '../../stores';
import { ErrorDisplay } from '../Common/ErrorDisplay';
import { SimpleFormField } from '../Common/FormField';
import TermsModal from './TermsModal';

interface FormData {
  name: string;
  email: string;
  password: string;
  confirmPassword: string;
}

interface TermsData {
  signature: string;
  printedName: string;
  date: string;
  email: string;
}

const Register: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { setUser, setToken } = useAuthStore();
  const [formData, setFormData] = useState<FormData>({
    name: '',
    email: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState<string>('');
  const [showPasswordRequirements, setShowPasswordRequirements] = useState(false);
  const [showTermsModal, setShowTermsModal] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [termsData, setTermsData] = useState<TermsData | null>(null);
  const [profileImage, setProfileImage] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const passwordFieldRef = useRef<HTMLDivElement>(null);
  const requirementsRef = useRef<HTMLDivElement>(null);
  const referralIdFromUrl = String(searchParams.get('ref') || '').trim();

  useEffect(() => {
    if (/^[a-f\d]{24}$/i.test(referralIdFromUrl)) {
      localStorage.setItem('referralId', referralIdFromUrl);
    }
  }, [referralIdFromUrl]);

  // Password requirements validation
  const passwordRequirements = {
    minLength: formData.password.length >= 8,
    hasUpperCase: /[A-Z]/.test(formData.password),
    hasLowerCase: /[a-z]/.test(formData.password),
    hasNumber: /[0-9]/.test(formData.password),
    hasSpecialChar: /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(formData.password),
  };

  const allRequirementsMet = Object.values(passwordRequirements).every(Boolean);

  // Close requirements tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        requirementsRef.current &&
        passwordFieldRef.current &&
        !requirementsRef.current.contains(event.target as Node) &&
        !passwordFieldRef.current.contains(event.target as Node)
      ) {
        setShowPasswordRequirements(false);
      }
    };

    if (showPasswordRequirements) {
      document.addEventListener('mousedown', handleClickOutside);
      return () => {
        document.removeEventListener('mousedown', handleClickOutside);
      };
    }
  }, [showPasswordRequirements]);

  const registerMutation = useApi.useRegister({
    onSuccess: (data) => {
      if (data.token && data.user) {
        localStorage.removeItem('referralId');
        setToken(data.token);
        setUser(data.user);
        if (data.user.role === 'master_pending') {
          navigate('/register/pending', {
            state: { email: data.user.email, role: data.user.role },
          });
        } else {
          navigate('/dashboard');
        }
      }
    },
    onError: (err: any) => {
      setError(err.response?.data?.error || 'Registration failed. Please try again.');
    },
  });

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value,
    });
    setError('');
  };

  const handleImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      if (file.size > 5 * 1024 * 1024) {
        setError('Image size must be less than 5MB');
        return;
      }
      if (!file.type.startsWith('image/')) {
        setError('Please select an image file');
        return;
      }
      setProfileImage(file);
      setImagePreview(URL.createObjectURL(file));
      setError('');
    }
  };

  const handleTermsAccept = (data: TermsData) => {
    setTermsData(data);
    setTermsAccepted(true);
    setShowTermsModal(false);
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setError('');

    // Validation
    if (!formData.name.trim()) {
      setError('Name is required');
      return;
    }
    if (!formData.email.trim()) {
      setError('Email is required');
      return;
    }
    if (!formData.password) {
      setError('Password is required');
      return;
    }

    // Check password requirements
    if (!passwordRequirements.minLength) {
      setError('Password must be at least 8 characters');
      return;
    }
    if (!passwordRequirements.hasUpperCase) {
      setError('Password must contain at least one uppercase letter');
      return;
    }
    if (!passwordRequirements.hasLowerCase) {
      setError('Password must contain at least one lowercase letter');
      return;
    }
    if (!passwordRequirements.hasNumber) {
      setError('Password must contain at least one number');
      return;
    }
    if (!passwordRequirements.hasSpecialChar) {
      setError('Password must contain at least one special character');
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match');
      return;
    }

    // Check terms acceptance
    if (!termsAccepted || !termsData) {
      setError('You must accept the Terms & Conditions to continue');
      return;
    }

    // Register as follower (default role) with terms data
    const formDataToSend = new FormData();
    formDataToSend.append('name', formData.name);
    formDataToSend.append('email', formData.email);
    formDataToSend.append('password', formData.password);
    formDataToSend.append('role', 'follower');
    formDataToSend.append('termsAccepted', 'true');
    formDataToSend.append('termsSignature', termsData.signature);
    formDataToSend.append('termsPrintedName', termsData.printedName);
    formDataToSend.append('termsEmail', termsData.email);

    const storedReferralId = String(localStorage.getItem('referralId') || '').trim();
    if (/^[a-f\d]{24}$/i.test(storedReferralId)) {
      formDataToSend.append('uplineId', storedReferralId);
    }

    if (profileImage) {
      formDataToSend.append('profileImage', profileImage);
    }

    registerMutation.mutate(formDataToSend);
  };

  return (
    <div className="relative min-h-screen flex items-center justify-center overflow-hidden bg-[#0a0c10] py-8 sm:py-10 px-4 sm:px-6 lg:px-8">
      <div
        aria-hidden
        className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[#246BF5]/20 blur-3xl"
      />
      <div
        aria-hidden
        className="pointer-events-none absolute -bottom-28 -left-16 h-64 w-64 rounded-full bg-[#246BF5]/10 blur-3xl"
      />

      <div className="relative w-full max-w-md space-y-6 sm:space-y-8">
        <div className="text-center space-y-3">
          <div className="flex justify-center">
            <img
              src="/trade-pilot-logo.png"
              alt="Trade Pilot"
              className="h-20 w-20 md:h-24 md:w-24 object-cover"
            />
          </div>
          <div>
            <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#246BF5]">
              Tradepilot AI
            </p>
            <h1 className="mt-2 text-2xl sm:text-3xl md:text-4xl font-semibold tracking-tight text-white">
              Trade <span className="text-[#246BF5]">Pilot</span>
            </h1>
            <p className="mt-2 text-sm text-white/55">Create your account to get started</p>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-[#246BF5]/25 bg-gradient-to-br from-[#246BF5]/10 via-[#111827] to-[#0f1420] px-4 py-6 sm:px-6 sm:py-8 md:px-8 shadow-[0_0_40px_rgba(36,107,245,0.08)]">
          {error && <ErrorDisplay error={error} onDismiss={() => setError('')} className="mb-5" />}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="flex flex-col items-center justify-center mb-2">
              <div className="relative group">
                <div className="w-24 h-24 rounded-full overflow-hidden border-2 border-[#246BF5]/40 bg-[#0f1420] flex items-center justify-center shadow-[0_0_24px_rgba(36,107,245,0.15)]">
                  {imagePreview ? (
                    <img src={imagePreview} alt="Preview" className="w-full h-full object-cover" />
                  ) : (
                    <FiUser className="w-12 h-12 text-white/35" />
                  )}
                </div>
                <label
                  htmlFor="profile-upload"
                  className="absolute bottom-0 right-0 bg-[#246BF5] p-2 rounded-full text-white cursor-pointer shadow-md hover:bg-[#1d5ad4] transition-colors"
                >
                  <FiCamera className="w-4 h-4" />
                  <input
                    id="profile-upload"
                    type="file"
                    className="hidden"
                    accept="image/*"
                    onChange={handleImageChange}
                  />
                </label>
              </div>
              <p className="text-xs text-white/40 mt-2">Upload profile image (optional)</p>
            </div>

            <SimpleFormField
              label="Full Name"
              name="name"
              icon={<FiUser className="h-5 w-5 text-[#246BF5]/70" />}
              value={formData.name}
              onChange={handleChange}
              required
              placeholder="John Doe"
            />

            <SimpleFormField
              label="Email Address"
              name="email"
              type="email"
              icon={<FiMail className="h-5 w-5 text-[#246BF5]/70" />}
              value={formData.email}
              onChange={handleChange}
              required
              placeholder="john@example.com"
            />

            <div ref={passwordFieldRef} className="space-y-2">
              <SimpleFormField
                label="Password"
                name="password"
                type="password"
                icon={<FiLock className="h-5 w-5 text-[#246BF5]/70" />}
                value={formData.password}
                onChange={handleChange}
                onFocus={() => setShowPasswordRequirements(true)}
                required
                placeholder="••••••••"
                showPasswordToggle={true}
              />

              {showPasswordRequirements && (
                <div
                  ref={requirementsRef}
                  className="relative z-10 w-full rounded-xl border border-[#246BF5]/20 bg-[#0f1420]/90 p-4 transition-all duration-200"
                >
                  <div className="mb-3 flex items-center gap-2">
                    <FiInfo className="h-5 w-5 text-[#246BF5]" />
                    <h3 className="text-sm font-semibold text-white">Password Requirements</h3>
                  </div>
                  <ul className="space-y-2">
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.minLength ? 'text-emerald-400' : 'text-white/40'}`}>
                      {passwordRequirements.minLength ? (
                        <FiCheck className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <FiX className="h-4 w-4 text-white/30" />
                      )}
                      <span>At least 8 characters</span>
                    </li>
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.hasUpperCase ? 'text-emerald-400' : 'text-white/40'}`}>
                      {passwordRequirements.hasUpperCase ? (
                        <FiCheck className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <FiX className="h-4 w-4 text-white/30" />
                      )}
                      <span>One uppercase letter (A-Z)</span>
                    </li>
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.hasLowerCase ? 'text-emerald-400' : 'text-white/40'}`}>
                      {passwordRequirements.hasLowerCase ? (
                        <FiCheck className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <FiX className="h-4 w-4 text-white/30" />
                      )}
                      <span>One lowercase letter (a-z)</span>
                    </li>
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.hasNumber ? 'text-emerald-400' : 'text-white/40'}`}>
                      {passwordRequirements.hasNumber ? (
                        <FiCheck className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <FiX className="h-4 w-4 text-white/30" />
                      )}
                      <span>One number (0-9)</span>
                    </li>
                    <li className={`flex items-center gap-2 text-sm ${passwordRequirements.hasSpecialChar ? 'text-emerald-400' : 'text-white/40'}`}>
                      {passwordRequirements.hasSpecialChar ? (
                        <FiCheck className="h-4 w-4 text-emerald-400" />
                      ) : (
                        <FiX className="h-4 w-4 text-white/30" />
                      )}
                      <span>One special character (!@#$%^&*...)</span>
                    </li>
                  </ul>
                </div>
              )}
            </div>

            <SimpleFormField
              label="Confirm Password"
              name="confirmPassword"
              type="password"
              icon={<FiShield className="h-5 w-5 text-[#246BF5]/70" />}
              value={formData.confirmPassword}
              onChange={handleChange}
              required
              placeholder="••••••••"
              showPasswordToggle={true}
            />

            <div className="space-y-3">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  id="terms"
                  checked={termsAccepted}
                  readOnly
                  className="mt-1 h-4 w-4 rounded border-white/20 bg-[#0f1420] text-[#246BF5] focus:ring-[#246BF5] cursor-pointer"
                />
                <label htmlFor="terms" className="text-sm text-white/70 flex-1 min-w-0">
                  I have read and agree to the{' '}
                  <button
                    type="button"
                    onClick={() => setShowTermsModal(true)}
                    className="text-[#246BF5] hover:text-[#4f8cff] font-semibold underline"
                  >
                    Terms & Conditions
                  </button>
                </label>
              </div>

              {termsAccepted && termsData && (
                <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3">
                  <div className="flex items-center gap-2 text-sm text-emerald-400">
                    <FiCheck className="h-4 w-4" />
                    <span className="font-semibold">Terms Accepted</span>
                  </div>
                  <p className="text-xs text-emerald-400/80 mt-1 break-words">
                    Signed by {termsData.printedName} on {termsData.date}
                  </p>
                </div>
              )}

              {!termsAccepted && (
                <button
                  type="button"
                  onClick={() => setShowTermsModal(true)}
                  className="w-full flex items-center justify-center gap-2 px-4 py-3 rounded-xl border border-[#246BF5]/50 text-[#246BF5] hover:bg-[#246BF5]/10 font-semibold transition-colors"
                >
                  <FiFileText className="h-5 w-5" />
                  <span>Read & Accept Terms</span>
                </button>
              )}
            </div>

            <button
              type="submit"
              disabled={registerMutation.isPending || !termsAccepted}
              className="w-full inline-flex items-center justify-center gap-2 rounded-xl bg-[#246BF5] px-5 py-3.5 text-sm font-semibold text-white shadow-[0_8px_24px_rgba(36,107,245,0.35)] hover:bg-[#1d5ad4] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {registerMutation.isPending ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-t-2 border-b-2 border-white" />
                  <span>Creating account...</span>
                </>
              ) : (
                <>
                  <FiUserPlus className="h-5 w-5" />
                  <span>Create Account</span>
                </>
              )}
            </button>

            <div className="text-center">
              <p className="text-sm text-white/50">
                Already have an account?{' '}
                <Link
                  to="/login"
                  className="font-semibold text-[#246BF5] hover:text-[#4f8cff] transition-colors"
                >
                  Sign in
                </Link>
              </p>
            </div>
          </form>
        </div>
      </div>

      {/* Terms Modal */}
      <TermsModal
        isOpen={showTermsModal}
        onClose={() => setShowTermsModal(false)}
        onAccept={handleTermsAccept}
        email={formData.email}
      />
    </div>
  );
};

export default Register;
